<template>
  <DepositAccountCard
    :status="status"
    :header-title="headerTitle"
    :header-text="headerText"
    :items="items"
    :logo-text="translate('depositAccountCard.leabank.image text')"
    :should-show-delete-button="shouldShowDeleteButton"
    :is-deleting-deposit="isDeletingDeposit"
    :should-show-sign-button="false"
    :should-show-termination-button="shouldShowTerminationButton"
    delete-button-text="Slett"
    :terminate-button-text="terminateButtonText"
    :logo="logo"
    @delete="deleteDeposit"
    @terminate="goToTerminationView"
  >
    <template v-if="status !== 'paid'" v-slot:contentText>
      <template v-if="status === 'ordered'">
        <p>Leietaker har mottatt e-post om å signere kontoavtalen.</p>
              
        <p>
          Så snart dette er gjort vil KID nummer bli generert. Leietaker vil da motta
          betalingsinformasjon på e-post.
        </p>
      </template>

      <template v-else-if="status === 'terminationStarted'">
        <p>
          Leietaker har mottatt en e-post med instruks for avslutning. Du vil motta en e-post når
          det er din tur å signere.
        </p> 

        <p>
          Du behøver ikke å trykke på avslutt igjen, dette vil medføre at leietaker mottar en ny
          e-post og må fullføre steg på nytt.
        </p>
      </template>

      <template v-else-if="status === 'terminationExpired'">
        <p>
          Avslutningen har nå utløpt og leietaker kan ikke avslå eller akseptere lenger.
        </p>
            
        <p>For å fortsette så må en ny avslutning startes ved å bruke knappen under.</p>
      </template>

      <template v-else-if="status === 'terminationAccepted'">
        <p>
          Konto er under avslutning. Dersom du skal signere for avslutning vil du bli
          informert om dette på e-postadressen som er registrert på depositumskontoen.
        </p>

        <p>
          NB: Vær obs på at det kan ta noen virkedager før status på kontoen oppdateres. Status
          vil endres til <strong>Avsluttet</strong> når Lea Bank har utført utbetaling av
          depositumskonto.
        </p>
      </template>

      <template v-else-if="status === 'terminationDeclined'">
        <p>Leietaker har mottatt en e-post med ditt oppgitte krav.</p>

        <p>
          Leietaker har avslått dette kravet. Du bør kontakte leietaker for å komme frem til
          en enighet, eller ta saken videre til HTU.
          <a
            href="https://www.htu.no/klage-og-tilsvar/send-inn-klage"
            rel="noopener"
            target="_blank"
          >
            Se her for mer informasjon
          </a>
        </p>

        <p>
          Dersom dere kommer frem til en enighet kan du påbegynne avslutning på ny ved å trykke
          på knappen under.
        </p>
      </template>

      <template v-else-if="status === 'partiallyPaid'">
        <p>
          Det er gjort innskudd på depositumskontoen. Dette beløpet samsvarer ikke med det
          som står oppført i kontoavtalen.
        </p>

        <p>
          Dersom dere har blitt enige om et nytt beløp så behøver du ikke å utføre noe handling.
        </p>
      </template>

      <template v-else-if="status === 'signed'">
        <p>
          Depositumskonto er signert av leietaker. Leietaker har mottatt betalingsinformasjon
          på e-post.
        </p> 

        <p>
          Så snart vi har fått bekreftelse fra banken på at depositum er innbetalt av
          leietaker, vil du motta en e-post om dette.
        </p>
      </template>

      <template v-else-if="status === 'ended'">
        <p>Depositumskonto er avsluttet korrekt.</p>

        <p>
          Merk at det kan ta 1-3 virkedager før midler er disponibel på konto(er) som ble
          oppgitt i avslutningen.
        </p>
      </template>
    </template>
  </DepositAccountCard>
</template>

<script>
import Vue from 'vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import DepositAccountCard from '@/components/deposit/depositAccount/DepositAccountCard.vue';
import { TenancyApi } from '@modules/husleieapi2';

export default Vue.extend({
  name: 'DepositAccountCardContainer',
  data() {
    return {
      isDeletingDeposit: false,
    };
  },
  props: {
    landlordId: {
      type: Number,
      required: true,
    },
    tenancyId: {
      type: Number,
      required: true,
    },
    deposit: {
      type: Object,
      required: true,
    },
  },
  mixins: [i18nCommon],
  components: {
    DepositAccountCard,
  },
  computed: {
    status() {
      const { termination } = this.deposit;

      if (this.deposit.status === 'ended' || (termination && !termination.currentBalance)) {
        return 'ended';
      }
      if (!this.deposit.meta.structuredReference) {
        return 'ordered';
      }
      if (termination && termination.answered === 1 && termination.accepted === 0) {
        return 'terminationDeclined';
      }
      if (termination && termination.answered === 0 && termination.daysUntilTerminationExpires === 0) {
        return 'terminationExpired';
      }
      if (termination && termination.answered === 1 && termination.accepted === 1) {
        return 'terminationAccepted';
      }
      if (termination) {
        return 'terminationStarted';
      }
      if (this.deposit.amount > 0 && this.deposit.amount < this.deposit.meta.agreedAmount) {
        return 'partiallyPaid';
      }
      if (this.deposit.amount >= this.deposit.meta.agreedAmount) {
        return 'paid';
      }
      if (this.deposit.meta.structuredReference) {
        return 'signed';
      }
      return undefined;
    },
    headerTitle() {
      return {
        ordered: 'Bestilt',
        terminationStarted: 'Avslutning påbegynt',
        terminationExpired: 'Avslutning utløpt',
        terminationAccepted: 'I avslutningsprosess',
        terminationDeclined: 'Krav avslått',
        partiallyPaid: 'Delvis innbetalt',
        paid: 'Innbetalt',
        signed: 'Signert',
        ended: 'Depositumskonto er avsluttet',
      }[this.status] || '';
    },
    headerText() {
      return {
        ordered: 'Depositumskonto er bestilt',
        terminationStarted: 'Utleier har initiert avslutning',
        terminationExpired: 'Avslutning må startes på nytt',
        terminationAccepted: 'Det er din tur til å signere for avslutning',
        terminationDeclined: 'Leietaker har avslått ditt krav',
        partiallyPaid: 'Innbetalt beløp samsvarer ikke med avtalt beløp',
        paid: 'Det er gjort en innbetaling på depositumskonto som stemmer etter deres avtale.',
        signed: 'Depositumskontoen er signert av leietaker',
        ended: 'Depositumskonto er avsluttet',
      }[this.status] || '';
    },
    items() {
      const { termination } = this.deposit;

      return [
        {
          title: this.translate('depositAccountCard.items.balance'),
          value: this.toCurrency('NOK', Math.ceil(this.deposit.amount)),
          icon: 'budicon-finance-project',
        },
        {
          title: this.translate('depositAccountCard.items.agreed deposit'),
          value: this.toCurrency('NOK', Math.ceil(this.deposit.meta.agreedAmount)),
          icon: 'budicon-savings',
        },
        this.status !== 'ordered' ? {
          title: this.translate('depositAccountCard.items.kid'),
          value: this.deposit.meta.structuredReference,
          icon: 'budicon-cheque',
          letterSpacing: true,
        } : undefined,
        termination && termination.answered === 0 ? {
          title: 'Avslutningen må gjennomføres innen',
          value: termination.daysUntilTerminationExpires > 0 ? `${termination.daysUntilTerminationExpires} dag(er)` : 'Utløpt',
          icon: 'budicon-hourglass',
        } : undefined,
      ].filter(item => item);
    },
    shouldShowDeleteButton() {
      return ['draft', 'initiated', 'created'].includes(this.deposit.status);
    },
    shouldShowTerminationButton() {
      return [
        'partiallyPaid',
        'paid',
        'terminationStarted',
        'terminationDeclined',
        'terminationAccepted',
        'terminationExpired',
      ].includes(this.status);
    },
    terminateButtonText() {
      if (['terminationStarted', 'terminationDeclined', 'terminationAccepted'].includes(this.status)) {
        return this.translate('depositAccountCard.leabank.button.terminate anew');
      }

      return this.translate('depositAccountCard.leabank.button.terminate');
    },
    logo() {
      return require('@/assets/leabank.png');
    },
  },
  methods: {
    async deleteDeposit() {
      this.isDeletingDeposit = true;

      try {
        const api = new TenancyApi();
        await api.deleteLandlordTenancyDeposit(this.landlordId, this.tenancyId, this.deposit.id);

        window.location.reload();
      } catch {
        this.$toast(
          'En feil oppstod med å slette depositum kontoen. Vennligst prøv igjen.',
          'danger',
        );
      } finally {
        this.isDeletingDeposit = false;
      }
    },
    goToTerminationView() {
      this.$router.push({
        name: 'tenancy_deposit_release_form_leabank',
        params: {
          depId: this.deposit.id,
        },
      });
    },
  },
});
</script>
